import { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import { ReloadOutlined } from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import { processingApi } from "../../api/processingApi";
import { ProcessingLine, ProcessingStatsResponse } from "../../types/processing.type";

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const fmt = (value: number) => (Number.isFinite(value) ? value.toFixed(2) : "—");

export default function ProcessingStats() {
  const [stats, setStats] = useState<ProcessingStatsResponse | null>(null);
  const [lines, setLines] = useState<ProcessingLine[]>([]);
  const [lineId, setLineId] = useState<string | undefined>(undefined);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(30, "day"), dayjs()]);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await processingApi.getStats({
        startDate: range[0].format("YYYY-MM-DD"),
        endDate: range[1].format("YYYY-MM-DD"),
        lineId,
      });
      setStats(data);
    } catch (error: any) {
      message.error(error?.response?.data?.error || "Не удалось загрузить статистику.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, lineId]);

  useEffect(() => {
    processingApi
      .getLines()
      .then((data) => setLines(data.lines || []))
      .catch(() => setLines([]));
  }, []);

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>Статистика переработки</Title>

      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <RangePicker
            format="DD.MM.YYYY"
            value={range as any}
            allowClear={false}
            onChange={(value) => value && setRange(value as [Dayjs, Dayjs])}
          />
          <Select
            allowClear
            placeholder="Все линии"
            style={{ minWidth: 240 }}
            value={lineId}
            onChange={setLineId}
            options={lines.map((l) => ({ value: l._id, label: l.name }))}
          />
          <Button icon={<ReloadOutlined />} onClick={fetchStats} loading={loading}>
            Обновить
          </Button>
        </Space>
      </Card>

      {/* ── Сводка ──────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={12} md={8} lg={4}>
          <Card loading={loading}>
            <Statistic title="Загрузок" value={stats?.totals.loadsCount ?? 0} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={5}>
          <Card loading={loading}>
            <Statistic title="Переработано" value={fmt(stats?.totals.totalInput ?? 0)} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={5}>
          <Card loading={loading}>
            <Statistic title="Потеряно всего" value={fmt(stats?.totals.totalLoss ?? 0)} />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={5}>
          <Card loading={loading}>
            <Statistic
              title="Средняя потеря"
              value={fmt(stats?.totals.averageLossPercent ?? 0)}
              suffix="%"
            />
          </Card>
        </Col>
        <Col xs={12} md={8} lg={5}>
          <Card loading={loading}>
            <Statistic
              title="Нарушений лимитов"
              value={stats?.totals.limitViolations ?? 0}
              valueStyle={{ color: (stats?.totals.limitViolations ?? 0) > 0 ? "#cf1322" : undefined }}
            />
          </Card>
        </Col>
      </Row>

      {/* ── Сверка начисления с фактом ──────────────────────────── */}
      {stats && stats.inputBalances.length > 0 && (
        <Card
          title="Начислено против переработано"
          style={{ marginBottom: 16 }}
          loading={loading}
        >
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 12 }}
            message="Ранний сигнал расхождения"
            description="Вход приходуется расчётной формулой, а расходуется по реальным весам. Если разница устойчиво растёт из периода в период — коэффициент начисления разошёлся с фактом, и на складе числится материал, которого физически нет."
          />

          <Table
            size="small"
            pagination={false}
            rowKey="itemName"
            dataSource={stats.inputBalances}
            columns={[
              { title: "Позиция", dataIndex: "itemName", key: "itemName" },
              {
                title: "Начислено формулой",
                key: "accrued",
                render: (_: any, r: any) => `${fmt(r.accrued)} ${r.unitOfMeasurement}`,
              },
              {
                title: "Переработано по факту",
                key: "consumed",
                render: (_: any, r: any) => `${fmt(r.consumed)} ${r.unitOfMeasurement}`,
              },
              {
                title: "Разница",
                key: "difference",
                render: (_: any, r: any) => (
                  <Space size={6}>
                    <Text strong style={{ color: Math.abs(r.differencePercent) > 10 ? "#cf1322" : undefined }}>
                      {fmt(r.difference)} {r.unitOfMeasurement}
                    </Text>
                    <Tag color={Math.abs(r.differencePercent) > 10 ? "error" : "default"}>
                      {fmt(r.differencePercent)} %
                    </Tag>
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      )}

      {/* ── Выход по линиям ─────────────────────────────────────── */}
      {stats?.lines.map((line) => (
        <Card key={line.lineId} title={line.lineName} style={{ marginBottom: 16 }}>
          <Space wrap size={24} style={{ marginBottom: 12 }}>
            <Statistic title="Загрузок" value={line.loadsCount} valueStyle={{ fontSize: 18 }} />
            <Statistic title="Вход" value={fmt(line.totalInput)} valueStyle={{ fontSize: 18 }} />
            <Statistic
              title="Средняя потеря"
              value={fmt(line.averageLossPercent)}
              suffix="%"
              valueStyle={{ fontSize: 18 }}
            />
            <Statistic
              title="Максимальная потеря"
              value={fmt(line.maxLossPercent)}
              suffix="%"
              valueStyle={{ fontSize: 18 }}
            />
          </Space>

          <Table
            size="small"
            pagination={false}
            rowKey="itemId"
            dataSource={line.outputs}
            columns={[
              { title: "Выход", dataIndex: "itemName", key: "itemName" },
              {
                title: "Всего получено",
                key: "totalQuantity",
                render: (_: any, o: any) => fmt(o.totalQuantity),
              },
              { title: "Норма", key: "normPercent", render: (_: any, o: any) => `${o.normPercent} %` },
              {
                title: "Средний факт",
                key: "averagePercent",
                render: (_: any, o: any) => `${fmt(o.averagePercent)} %`,
              },
              {
                title: "Отклонение от нормы",
                key: "deviation",
                render: (_: any, o: any) => (
                  <Tag color={Math.abs(o.deviation) > 3 ? "warning" : "default"}>
                    {o.deviation > 0 ? "+" : ""}
                    {fmt(o.deviation)} п.п.
                  </Tag>
                ),
              },
            ]}
          />

          <Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 8 }}>
            Устойчивое отклонение среднего от нормы — повод пересчитать норму и коридор этого
            потока в карточке линии.
          </Text>
        </Card>
      ))}

      {stats && stats.lines.length === 0 && !loading && (
        <Alert type="info" showIcon message="За выбранный период загрузок не было." />
      )}
    </div>
  );
}
